import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const ADMIN_API = `${BACKEND_URL}/api/admin`;
const TOKEN_KEY = "bef_admin_token";

export function getAdminToken() {
  return localStorage.getItem(TOKEN_KEY);
}
export function setAdminToken(t) {
  if (t) localStorage.setItem(TOKEN_KEY, t);
  else localStorage.removeItem(TOKEN_KEY);
}

const client = axios.create({ baseURL: ADMIN_API });
client.interceptors.request.use((cfg) => {
  const t = getAdminToken();
  if (t) cfg.headers["X-Admin-Token"] = t;
  return cfg;
});

export async function adminLogin(email, password) {
  const { data } = await axios.post(`${ADMIN_API}/login`, { email, password });
  setAdminToken(data.token);
  if (data.email) localStorage.setItem("bef_admin_email", data.email);
  return data;
}

export function getAdminEmail() {
  return localStorage.getItem("bef_admin_email") || "";
}

export async function fetchDashboard() {
  const { data } = await client.get("/dashboard");
  return data;
}

export async function fetchDelegates(status = "paid") {
  const { data } = await client.get("/delegates", { params: { status } });
  return data;
}

export async function fetchLeads(params = {}) {
  const { data } = await client.get("/leads", { params });
  return data;
}

export async function patchSubmission(id, patch) {
  const { data } = await client.patch(`/submissions/${id}`, patch);
  return data;
}

export async function deleteSubmission(id) {
  const { data } = await client.delete(`/submissions/${id}`);
  return data;
}

export async function bulkDeleteSubmissions(ids) {
  const { data } = await client.post("/submissions/bulk-delete", { ids });
  return data;
}

export async function purgeTestEntries() {
  const { data } = await client.post("/submissions/purge-test");
  return data;
}

export async function manualAddDelegate(payload) {
  const { data } = await client.post("/delegates/manual", payload);
  return data;
}

export async function fetchExhibitors() {
  const { data } = await client.get("/exhibitors");
  return data;
}

export async function createExhibitor(payload) {
  const { data } = await client.post("/exhibitors", payload);
  return data;
}

export async function updateExhibitor(id, payload) {
  const { data } = await client.patch(`/exhibitors/${id}`, payload);
  return data;
}

export async function deleteExhibitor(id) {
  const { data } = await client.delete(`/exhibitors/${id}`);
  return data;
}

export async function sendRecoveryEmail(id) {
  const { data } = await client.post(`/recovery-email/${id}`);
  return data;
}

export async function resendConfirmation(id) {
  const { data } = await client.post(`/resend-confirmation/${id}`);
  return data;
}

// ---- CSV helpers ----
export function toCSV(rows, columns) {
  const header = columns.map((c) => `"${c.label.replace(/"/g, '""')}"`).join(",");
  const body = rows
    .map((r) =>
      columns
        .map((c) => {
          const v = typeof c.get === "function" ? c.get(r) : r[c.key];
          const s = v == null ? "" : String(v);
          return `"${s.replace(/"/g, '""')}"`;
        })
        .join(",")
    )
    .join("\n");
  return `${header}\n${body}`;
}

export function downloadCSV(filename, csv) {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ---- Excel export ----
export async function downloadXLSX(filename, rows, columns) {
  const XLSX = await import("xlsx");
  const data = rows.map((r) => {
    const obj = {};
    columns.forEach((c) => {
      obj[c.label] = typeof c.get === "function" ? c.get(r) : r[c.key];
    });
    return obj;
  });
  const ws = XLSX.utils.json_to_sheet(data);
  // auto-fit column widths (rough)
  const cols = columns.map((c) => ({
    wch: Math.min(60, Math.max(c.label.length, ...data.slice(0, 200).map((d) => String(d[c.label] ?? "").length))),
  }));
  ws["!cols"] = cols;
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Leads");
  XLSX.writeFile(wb, filename);
}
